// Vendor → Müşteri Detay Sayfası (sekmeli)
// Sekmeler: Genel, Kullanım, Ödemeler, Mesajlar, Notlar, Aktivite
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, EmptyState } from '../components/ui';
import { useAuth } from '../context/AuthContext';

const fmt   = (n) => new Intl.NumberFormat('tr-TR', { style:'currency', currency:'TRY', minimumFractionDigits: 0 }).format(Number(n) || 0);
const fmtN  = (n) => new Intl.NumberFormat('tr-TR').format(n || 0);
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('tr-TR') : '—';
const fmtDateTime = (s) => s ? new Date(s).toLocaleString('tr-TR') : '—';

const HEALTH_BADGES = {
  healthy:  { label: 'Sağlıklı',  clr: '#059669', bg: 'rgba(16,185,129,0.12)', dot: '🟢' },
  warning:  { label: 'Uyarı',     clr: '#d97706', bg: 'rgba(245,158,11,0.12)', dot: '🟡' },
  at_risk:  { label: 'Riskli',    clr: '#ea580c', bg: 'rgba(234,88,12,0.12)',  dot: '🟠' },
  critical: { label: 'Kritik',    clr: '#dc2626', bg: 'rgba(239,68,68,0.12)',  dot: '🔴' },
};

const STATUS_LABEL = {
  active: 'Aktif', trial: 'Trial', trial_expired: 'Trial Bitti',
  past_due: 'Vade Geçti', suspended: 'Askıda', cancelled: 'İptal',
};
const STATUS_BG = {
  active: 'rgba(16,185,129,0.12)', trial: 'rgba(59,130,246,0.12)',
  trial_expired: 'rgba(245,158,11,0.12)', past_due: 'rgba(245,158,11,0.12)',
  suspended: 'rgba(239,68,68,0.12)', cancelled: 'rgba(100,116,139,0.12)',
};
const STATUS_CLR = {
  active: '#059669', trial: '#2563eb', trial_expired: '#d97706',
  past_due: '#d97706', suspended: '#dc2626', cancelled: '#64748b',
};

const TABS = [
  { id: 'general',  label: 'Genel',     icon: '👤' },
  { id: 'usage',    label: 'Kullanım',  icon: '📊' },
  { id: 'billing',  label: 'Ödemeler',  icon: '💳' },
  { id: 'messages', label: 'Mesajlar',  icon: '✉️' },
  { id: 'notes',    label: 'Notlar',    icon: '📝' },
  { id: 'activity', label: 'Aktivite',  icon: '📋' },
];

export default function VendorCustomerDetailPage({ companyId, onBack }) {
  const { startImpersonation } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [data, setData]   = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [modal, setModal] = useState(null); // 'discount', 'extend', 'message'

  const load = useCallback(async () => {
    if (!companyId) { setError('Firma ID bulunamadı'); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      // Ana detay zorunlu
      const d = await api.get('/vendor/companies/' + companyId);
      if (!d.data.success) throw new Error(d.data.message || 'Veri alınamadı');
      setData(d.data.data);
    } catch (e) {
      console.error('detail load:', e);
      setError(e?.response?.data?.message || e.message || 'Bağlantı hatası');
      setLoading(false);
      return;
    }
    // Health bağımsız — başarısız olsa bile detay sayfası açılsın
    try {
      const h = await api.get('/vendor/companies/' + companyId + '/health');
      if (h.data.success) setHealth(h.data.data);
    } catch (e) { console.warn('health load failed:', e?.response?.status); }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const doImpersonate = async () => {
    if (!window.confirm(`"${data?.company?.name}" firmasına müşteri olarak giriş yapılacak. Devam edilsin mi?`)) return;
    try {
      const r = await api.post('/vendor/companies/' + companyId + '/impersonate');
      if (r.data.success) startImpersonation(r.data.data.token);
    } catch (err) { alert(err.response?.data?.message || 'Hata'); }
  };

  if (loading) return <div style={{ padding: 40, textAlign:'center', color:'var(--text-tertiary)' }}>Yükleniyor...</div>;
  if (error) return (
    <div style={{ padding: 24 }}>
      <Button size="sm" variant="ghost" onClick={onBack}>← Geri</Button>
      <div style={{ marginTop: 12, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: 16, color: 'var(--danger)' }}>
        <strong>Detay yüklenemedi:</strong> {error}
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-tertiary)' }}>Firma ID: {companyId || '(boş)'}</div>
      </div>
      <div style={{ marginTop: 12 }}><Button size="sm" onClick={load}>Tekrar Dene</Button></div>
    </div>
  );
  if (!data) return null;

  const c = data.company || data;
  const badge = HEALTH_BADGES[health?.health_score] || HEALTH_BADGES.healthy;

  return (
    <div style={{ padding: 0 }}>
      {/* Üst header — başlık + status + aksiyon butonları */}
      <div style={{ background:'var(--bg-secondary)', borderBottom:'1px solid var(--border-secondary)', padding:'18px 24px' }}>
        <div style={{ display:'flex', alignItems:'center', gap: 8, marginBottom: 8, fontSize:'0.82rem', color:'var(--text-secondary)' }}>
          <span style={{ cursor:'pointer', color:'var(--accent-primary)' }} onClick={onBack}>← Firmalar</span>
          <span>/</span>
          <span style={{ color:'var(--text-primary)', fontWeight:600 }}>{c.name}</span>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap: 12 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: 4 }}>
              <h1 style={{ margin: 0, fontSize:'1.4rem', fontWeight:700, color:'var(--text-primary)', letterSpacing:'-0.02em' }}>
                {c.name}
              </h1>
              <span style={{ padding:'3px 10px', borderRadius: 100, fontSize:'0.7rem', fontWeight:700,
                             background: STATUS_BG[c.subscription_status], color: STATUS_CLR[c.subscription_status],
                             textTransform: 'uppercase', letterSpacing:'0.04em' }}>
                {STATUS_LABEL[c.subscription_status] || c.subscription_status}
              </span>
              <span style={{ padding:'3px 10px', borderRadius: 100, fontSize:'0.75rem', fontWeight:600,
                             background: badge.bg, color: badge.clr }}>
                {badge.dot} {badge.label}
              </span>
            </div>
            <div style={{ display:'flex', gap: 16, fontSize:'0.8rem', color:'var(--text-secondary)', flexWrap:'wrap' }}>
              <span><b style={{ color:'var(--text-primary)' }}>{c.code}</b></span>
              {c.tax_number && <span>VKN <b style={{ color:'var(--text-primary)' }}>{c.tax_number}</b></span>}
              {c.tax_office && <span>{c.tax_office}</span>}
              {c.email && <span style={{ color:'var(--accent-primary)' }}>{c.email}</span>}
              {c.phone && <span>{c.phone}</span>}
            </div>
          </div>
          <div style={{ display:'flex', gap: 6, flexWrap:'wrap' }}>
            <Button variant="primary" onClick={doImpersonate}>🎭 Müşteri Olarak Gir</Button>
            <Button variant="ghost" onClick={() => setModal('edit')}>✏️ Düzenle</Button>
            <Button variant="ghost" onClick={() => setModal('extend')}>⏰ Trial Uzat</Button>
            <Button variant="ghost" onClick={() => setModal('discount')}>💸 Manuel İskonto</Button>
            <Button variant="ghost" onClick={() => setModal('message')}>✉️ Mesaj Gönder</Button>
          </div>
        </div>
      </div>

      {/* Sekme bar */}
      <div style={{ display:'flex', borderBottom:'1px solid var(--border-secondary)', background:'var(--bg-primary)',
                    overflowX:'auto' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
                  style={{ padding:'12px 18px', border:'none', background:'transparent', cursor:'pointer',
                           fontSize:'0.85rem', fontWeight: activeTab === t.id ? 600 : 500,
                           color: activeTab === t.id ? 'var(--accent-primary)' : 'var(--text-secondary)',
                           borderBottom: activeTab === t.id ? '2px solid var(--accent-primary)' : '2px solid transparent',
                           transition:'all 0.12s', whiteSpace:'nowrap',
                           display:'inline-flex', alignItems:'center', gap: 6 }}>
            <span>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {/* İçerik */}
      <div style={{ padding: 18, maxWidth: 1280 }}>
        {activeTab === 'general'  && <GeneralTab c={c} health={health} />}
        {activeTab === 'usage'    && <UsageTab health={health} c={c} />}
        {activeTab === 'billing'  && <BillingTab companyId={companyId} />}
        {activeTab === 'messages' && <MessagesTab companyId={companyId} onSend={() => setModal('message')} />}
        {activeTab === 'notes'    && <NotesTab companyId={companyId} />}
        {activeTab === 'activity' && <ActivityTab companyId={companyId} />}
      </div>

      {modal === 'edit'     && <EditCompanyModal company={c} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal === 'extend'   && <ExtendTrialModal company={c} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal === 'discount' && <DiscountModal    company={c} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal === 'message'  && <SendMessageModal company={c} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
    </div>
  );
}

/* ── DÜZENLE MODALI — firma bilgileri ──────────────────────────── */
function EditCompanyModal({ company, onClose, onDone }) {
  const [form, setForm] = useState({
    name: company.name || '',
    tax_number: company.tax_number || '',
    tax_office: company.tax_office || '',
    address: company.address || '',
    city: company.city || '',
    phone: company.phone || '',
    email: company.email || '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setErr(null);
    if (!form.name.trim()) return setErr('Firma ünvanı zorunlu');
    setBusy(true);
    try {
      await api.put('/vendor/companies/' + company.id, form);
      onDone();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Hata');
      setBusy(false);
    }
  };

  return (
    <Modal title={`Firma Bilgilerini Düzenle — ${company.code}`} onClose={onClose}>
      <div style={{ padding: 18, display:'flex', flexDirection:'column', gap: 12, maxWidth: 560 }}>
        <FormField label="Ünvan" required>
          <input className="ui-input" value={form.name} onChange={e => f('name', e.target.value)} autoFocus />
        </FormField>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
          <FormField label="Vergi No">
            <input className="ui-input" value={form.tax_number} onChange={e => f('tax_number', e.target.value)} />
          </FormField>
          <FormField label="Vergi Dairesi">
            <input className="ui-input" value={form.tax_office} onChange={e => f('tax_office', e.target.value)} />
          </FormField>
        </div>
        <FormField label="Adres">
          <textarea className="ui-input" rows={2} value={form.address} onChange={e => f('address', e.target.value)} />
        </FormField>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
          <FormField label="Şehir">
            <input className="ui-input" value={form.city} onChange={e => f('city', e.target.value)} />
          </FormField>
          <FormField label="Telefon">
            <input className="ui-input" value={form.phone} onChange={e => f('phone', e.target.value)} />
          </FormField>
        </div>
        <FormField label="E-posta">
          <input className="ui-input" type="email" value={form.email} onChange={e => f('email', e.target.value)} />
        </FormField>
        {err && <div style={{ color:'var(--danger)', fontSize: 13 }}>{err}</div>}
        <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', marginTop: 8 }}>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>{busy ? '...' : 'Kaydet'}</Button>
        </div>
      </div>
    </Modal>
  );
}

/* ── GENEL Sekmesi ──────────────────────────────────────────────── */
function GeneralTab({ c, health }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
      <Card title="🏢 Firma Bilgisi">
        <Row label="Ünvan"        value={c.name} />
        <Row label="Kod"          value={c.code} mono />
        <Row label="Vergi No"     value={c.tax_number} mono />
        <Row label="Vergi Dairesi" value={c.tax_office} />
        <Row label="Adres"        value={c.address} />
        <Row label="Şehir"        value={c.city} />
        <Row label="E-posta"      value={c.email} />
        <Row label="Telefon"      value={c.phone} />
      </Card>

      <Card title="💎 Abonelik">
        <Row label="Plan"          value={c.plan_name || '—'} />
        <Row label="Durum"         value={STATUS_LABEL[c.subscription_status] || c.subscription_status} />
        <Row label="Periyot"       value={c.billing_period === 'yearly' ? 'Yıllık' : 'Aylık'} />
        <Row label="Aylık Tutar"   value={fmt(c.monthly_price)} mono />
        <Row label="Yıllık Tutar"  value={fmt(c.yearly_price)} mono />
        {c.custom_monthly_price && <Row label="Özel Fiyat" value={fmt(c.custom_monthly_price)} mono accent />}
        {c.manual_discount_pct && <Row label="Manuel İndirim" value={`%${c.manual_discount_pct} (${fmtDate(c.manual_discount_until)}'e kadar)`} accent />}
        <Row label="Trial Bitiş"   value={fmtDate(c.trial_ends_at)} />
        <Row label="Abone Bitiş"   value={fmtDate(c.subscription_ends_at)} />
        <Row label="Auto Renew"    value={c.auto_renew ? '✓ Açık' : 'Kapalı'} />
      </Card>

      <Card title="📊 Kayıt & Aktivite">
        <Row label="Kayıt Tarihi"   value={fmtDateTime(c.created_at)} />
        <Row label="Kayıttan beri"  value={health ? `${health.days_since_signup} gün` : '—'} />
        <Row label="Son Giriş"      value={fmtDateTime(health?.last_login_at)} />
        <Row label="Son Belge"      value={fmtDate(health?.last_invoice_date)} />
        <Row label="Lifetime Ödenen" value={fmt(health?.lifetime_paid)} mono />
        {c.referral_source && <Row label="Geliş Kaynağı" value={c.referral_source} />}
        {c.admin_tags && <Row label="Etiketler" value={c.admin_tags} />}
      </Card>
    </div>
  );
}

/* ── KULLANIM Sekmesi ───────────────────────────────────────────── */
function UsageTab({ health, c }) {
  if (!health) return <div style={{ color:'var(--text-tertiary)' }}>Veri yok</div>;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap: 14 }}>
      <Card title="📊 Kullanım Metrikleri">
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
          <Metric label="Aktif Kullanıcı"          value={fmtN(health.active_user_count)} sub={`son 30g: ${health.users_last_30d || 0}`} />
          <Metric label="Cari Sayısı"              value={fmtN(health.account_count)} />
          <Metric label="Stok Sayısı"              value={fmtN(health.stock_count)} />
          <Metric label="Son 30g Belge"            value={fmtN(health.docs_last_30d)} accent />
          <Metric label="Kayıttan Beri"            value={`${health.days_since_signup}g`} />
          <Metric label="Son Girişten Beri"        value={health.days_since_login != null ? `${health.days_since_login}g` : '—'}
                  warn={health.days_since_login > 14} danger={health.days_since_login > 30} />
        </div>
      </Card>

      {health.health_reasons && health.health_reasons.length > 0 && (
        <Card title="⚠ Sağlık Sorunları">
          {health.health_reasons.map((r, i) => (
            <div key={i} style={{ padding:'6px 10px', background:'rgba(245,158,11,0.10)',
                                   border:'1px solid rgba(245,158,11,0.25)', borderRadius: 6, marginBottom: 6,
                                   fontSize:'0.85rem', color:'#92400e' }}>
              {r}
            </div>
          ))}
        </Card>
      )}

      {health.modules && health.modules.length > 0 && (
        <Card title="🧩 Aktif Modüller">
          <div style={{ display:'flex', flexWrap:'wrap', gap: 6 }}>
            {health.modules.map(m => (
              <span key={m.module_key} style={{ padding:'4px 10px', borderRadius: 100, fontSize:'0.78rem', fontWeight: 600,
                                                 background: m.is_enabled ? 'rgba(16,185,129,0.12)' : 'rgba(100,116,139,0.10)',
                                                 color: m.is_enabled ? 'var(--success)' : 'var(--text-tertiary)' }}>
                {m.module_key}{!m.is_enabled && ' (pasif)'}
              </span>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ── ÖDEMELER Sekmesi ───────────────────────────────────────────── */
function BillingTab({ companyId }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/vendor/invoices', { params: { company_id: companyId, limit: 100 } })
      .then(r => { setInvoices(r.data.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [companyId]);

  if (loading) return <div>Yükleniyor...</div>;
  if (invoices.length === 0) return <EmptyState title="Henüz fatura yok" />;

  return (
    <Card title="💳 Vendor Faturaları (kestiğimiz)">
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
        <thead>
          <tr style={{ borderBottom:'1px solid var(--border-secondary)' }}>
            <th style={{ padding:'8px 10px', textAlign:'left', color:'var(--text-tertiary)', fontWeight:600, fontSize:'0.7rem', textTransform:'uppercase' }}>Tarih</th>
            <th style={{ padding:'8px 10px', textAlign:'left', color:'var(--text-tertiary)', fontWeight:600, fontSize:'0.7rem', textTransform:'uppercase' }}>Dönem</th>
            <th style={{ padding:'8px 10px', textAlign:'right', color:'var(--text-tertiary)', fontWeight:600, fontSize:'0.7rem', textTransform:'uppercase' }}>Tutar</th>
            <th style={{ padding:'8px 10px', textAlign:'center', color:'var(--text-tertiary)', fontWeight:600, fontSize:'0.7rem', textTransform:'uppercase' }}>Durum</th>
            <th style={{ padding:'8px 10px', textAlign:'left', color:'var(--text-tertiary)', fontWeight:600, fontSize:'0.7rem', textTransform:'uppercase' }}>Yöntem</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map(inv => (
            <tr key={inv.id} style={{ borderBottom:'1px solid var(--border-secondary)' }}>
              <td style={{ padding:'10px', fontFamily:'monospace' }}>{fmtDate(inv.invoice_date)}</td>
              <td style={{ padding:'10px', fontSize:'0.75rem', color:'var(--text-tertiary)' }}>
                {inv.period_start ? `${fmtDate(inv.period_start)} - ${fmtDate(inv.period_end)}` : '—'}
              </td>
              <td style={{ padding:'10px', textAlign:'right', fontFamily:'monospace', fontWeight: 600 }}>{fmt(inv.total_amount)}</td>
              <td style={{ padding:'10px', textAlign:'center' }}>
                {inv.status === 'paid'      && <span style={{ color:'var(--success)', fontSize:'0.75rem', fontWeight:600 }}>● Ödendi</span>}
                {inv.status === 'pending'   && <span style={{ color:'#d97706', fontSize:'0.75rem', fontWeight:600 }}>● Bekliyor</span>}
                {inv.status === 'overdue'   && <span style={{ color:'var(--danger)', fontSize:'0.75rem', fontWeight:600 }}>● Gecikti</span>}
                {inv.status === 'cancelled' && <span style={{ color:'#64748b', fontSize:'0.75rem', fontWeight:600 }}>● İptal</span>}
              </td>
              <td style={{ padding:'10px', fontSize:'0.75rem' }}>{inv.payment_method || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/* ── MESAJLAR Sekmesi ───────────────────────────────────────────── */
function MessagesTab({ companyId, onSend }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/vendor/companies/' + companyId + '/messages')
      .then(r => { setMessages(r.data.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [companyId]);

  if (loading) return <div>Yükleniyor...</div>;

  return (
    <Card title="✉️ Mesaj Geçmişi" action={<Button size="sm" onClick={onSend}>+ Yeni Mesaj</Button>}>
      {messages.length === 0 ? (
        <div style={{ padding: 20, textAlign:'center', color:'var(--text-tertiary)' }}>Henüz mesaj gönderilmemiş</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap: 8 }}>
          {messages.map(m => (
            <div key={m.id} style={{ padding: 12, background:'var(--bg-secondary)', border:'1px solid var(--border-secondary)', borderRadius: 6 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom: 6 }}>
                <div>
                  <strong style={{ fontSize:'0.88rem' }}>{m.subject || '(konu yok)'}</strong>
                  <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)', marginTop: 2 }}>
                    {m.channel} · {m.to_address} · {m.sent_by_name}
                  </div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <span style={{ fontSize:'0.7rem', fontWeight:600, padding:'2px 8px', borderRadius:100,
                                 background: m.status === 'sent' ? 'rgba(16,185,129,0.12)' : m.status === 'failed' ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
                                 color: m.status === 'sent' ? '#059669' : m.status === 'failed' ? '#dc2626' : '#d97706' }}>
                    {m.status === 'sent' ? 'Gönderildi' : m.status === 'failed' ? 'Başarısız' : 'Bekliyor'}
                  </span>
                  <div style={{ fontSize:'0.7rem', color:'var(--text-tertiary)', marginTop: 4 }}>{fmtDateTime(m.created_at)}</div>
                </div>
              </div>
              <div style={{ fontSize:'0.82rem', color:'var(--text-secondary)', maxHeight: 80, overflow:'hidden' }}
                   dangerouslySetInnerHTML={{ __html: m.body }} />
              {m.error_message && <div style={{ marginTop: 6, color:'var(--danger)', fontSize:'0.75rem' }}>⚠ {m.error_message}</div>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ── NOTLAR Sekmesi ─────────────────────────────────────────────── */
function NotesTab({ companyId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/vendor/companies/' + companyId + '/notes')
      .then(r => { setNotes(r.data.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await api.post('/vendor/companies/' + companyId + '/notes', { content, is_pinned: pinned });
      setContent(''); setPinned(false); load();
    } catch (err) { alert(err.response?.data?.message || 'Hata'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Not silinsin mi?')) return;
    await api.delete('/vendor/companies/' + companyId + '/notes/' + id);
    load();
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap: 14 }}>
      <Card title="📝 Yeni Not">
        <textarea value={content} onChange={e => setContent(e.target.value)} className="ui-input"
                  rows={3} placeholder="Görüşme notu, müşteri talebi, hatırlatma..."
                  style={{ resize:'vertical', minHeight: 80 }} />
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop: 8 }}>
          <label style={{ fontSize:'0.82rem', display:'inline-flex', alignItems:'center', gap: 6 }}>
            <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} />
            📌 Sabit (üstte tut)
          </label>
          <Button size="sm" variant="primary" onClick={save} disabled={!content.trim() || saving}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </Card>

      {loading ? <div>Yükleniyor...</div> : notes.length === 0 ? (
        <EmptyState title="Henüz not yok" subtitle="Müşteri görüşmelerinden, eski notları buraya yazın" />
      ) : (
        notes.map(n => (
          <div key={n.id} style={{ padding: 12, background: n.is_pinned ? 'rgba(245,158,11,0.06)' : 'var(--bg-secondary)',
                                   border:'1px solid ' + (n.is_pinned ? 'rgba(245,158,11,0.25)' : 'var(--border-secondary)'),
                                   borderRadius: 6 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom: 6 }}>
              <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)' }}>
                {n.is_pinned && '📌 '}{n.author_name || '—'} · {fmtDateTime(n.created_at)}
              </div>
              <button onClick={() => del(n.id)} style={{ background:'none', border:'none', color:'var(--text-tertiary)', cursor:'pointer', fontSize:'0.85rem' }}>×</button>
            </div>
            <div style={{ fontSize:'0.85rem', color:'var(--text-primary)', whiteSpace:'pre-wrap' }}>{n.content}</div>
          </div>
        ))
      )}
    </div>
  );
}

/* ── AKTİVİTE Sekmesi ───────────────────────────────────────────── */
function ActivityTab({ companyId }) {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/vendor/companies/' + companyId + '/activity')
      .then(r => { setActivity(r.data.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [companyId]);
  if (loading) return <div>Yükleniyor...</div>;
  if (activity.length === 0) return <EmptyState title="Henüz aktivite yok" />;
  return (
    <Card title="📋 Admin Aktivite Log'u">
      {activity.map(a => (
        <div key={a.id} style={{ display:'flex', gap: 12, padding:'10px 0', borderBottom:'1px solid var(--border-secondary)' }}>
          <div style={{ width: 8, height: 8, borderRadius:'50%', background:'var(--accent-primary)', flexShrink: 0, marginTop: 8 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize:'0.85rem', fontWeight: 600 }}>{a.action.replace(/_/g, ' ')}</div>
            <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)' }}>
              {a.admin_name} · {fmtDateTime(a.created_at)} {a.ip_address && '· ' + a.ip_address}
            </div>
            {a.details && typeof a.details === 'object' && (
              <pre style={{ fontSize:'0.72rem', color:'var(--text-secondary)', marginTop: 4,
                            padding: 6, background:'var(--bg-secondary)', borderRadius: 4, overflowX:'auto' }}>
                {JSON.stringify(a.details, null, 2)}
              </pre>
            )}
          </div>
        </div>
      ))}
    </Card>
  );
}

/* ── Helper components ──────────────────────────────────────────── */
function Card({ title, action, children }) {
  return (
    <div style={{ background:'var(--bg-secondary)', border:'1px solid var(--border-secondary)', borderRadius: 8 }}>
      {title && (
        <div style={{ padding:'10px 14px', borderBottom:'1px solid var(--border-secondary)',
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:'0.85rem', fontWeight:600 }}>{title}</span>
          {action}
        </div>
      )}
      <div style={{ padding: 14 }}>{children}</div>
    </div>
  );
}

function Row({ label, value, mono, accent }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'5px 0',
                  borderBottom:'1px dashed var(--border-secondary)' }}>
      <span style={{ fontSize:'0.76rem', color:'var(--text-tertiary)' }}>{label}</span>
      <span style={{ fontSize:'0.85rem', color: accent ? 'var(--accent-primary)' : 'var(--text-primary)', fontWeight: accent ? 600 : 400,
                     fontFamily: mono ? 'monospace' : 'inherit', textAlign:'right' }}>{value || '—'}</span>
    </div>
  );
}

function Metric({ label, value, sub, accent, warn, danger }) {
  const clr = danger ? 'var(--danger)' : warn ? '#d97706' : accent ? 'var(--accent-primary)' : 'var(--text-primary)';
  return (
    <div style={{ padding:'10px 14px', background:'var(--bg-primary)', border:'1px solid var(--border-secondary)',
                  borderRadius: 6, borderLeft: '3px solid ' + clr }}>
      <div style={{ fontSize:'0.7rem', color:'var(--text-tertiary)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize:'1.3rem', fontWeight:700, color: clr, fontVariantNumeric:'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize:'0.7rem', color:'var(--text-tertiary)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/* ── MODALLAR ───────────────────────────────────────────────────── */
function ExtendTrialModal({ company, onClose, onDone }) {
  const [days, setDays] = useState(14);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/vendor/companies/' + company.id + '/extend-trial', { days: parseInt(days), reason });
      onDone();
    } catch (err) { alert(err.response?.data?.message || 'Hata'); setBusy(false); }
  };
  return (
    <Modal title="⏰ Trial Süresini Uzat" onClose={onClose}>
      <div style={{ padding: 18, display:'flex', flexDirection:'column', gap: 12 }}>
        <FormField label="Gün Sayısı"><input className="ui-input" type="number" min={1} max={365} value={days} onChange={e => setDays(e.target.value)} /></FormField>
        <FormField label="Sebep (opsiyonel)"><input className="ui-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="Müşteri özel talebi..." /></FormField>
        <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', marginTop: 8 }}>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>{busy ? '...' : 'Uzat'}</Button>
        </div>
      </div>
    </Modal>
  );
}

function DiscountModal({ company, onClose, onDone }) {
  const [percent, setPercent] = useState(20);
  const [months, setMonths]   = useState(3);
  const [reason, setReason]   = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (percent < 0 || percent > 100) return alert('Yüzde 0-100 arası olmalı');
    setBusy(true);
    const until = new Date();
    until.setMonth(until.getMonth() + parseInt(months || 1));
    try {
      await api.post('/vendor/companies/' + company.id + '/manual-discount',
        { percent: parseFloat(percent), until: until.toISOString(), reason });
      onDone();
    } catch (err) { alert(err.response?.data?.message || 'Hata'); setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Manuel iskontoyu kaldır?')) return;
    setBusy(true);
    try { await api.delete('/vendor/companies/' + company.id + '/manual-discount'); onDone(); }
    catch (err) { alert(err.response?.data?.message || 'Hata'); setBusy(false); }
  };
  return (
    <Modal title="💸 Manuel İskonto" onClose={onClose}>
      <div style={{ padding: 18, display:'flex', flexDirection:'column', gap: 12 }}>
        {company.manual_discount_pct && (
          <div style={{ padding: 10, background:'rgba(124,58,237,0.10)', border:'1px solid rgba(124,58,237,0.30)', borderRadius: 6, fontSize:'0.85rem' }}>
            Şu an aktif: <b>%{company.manual_discount_pct}</b> ({fmtDate(company.manual_discount_until)}'e kadar)
            <Button size="sm" variant="ghost" onClick={remove} style={{ marginLeft: 8, color:'var(--danger)' }}>Kaldır</Button>
          </div>
        )}
        <FormField label="İskonto Yüzdesi"><input className="ui-input" type="number" min={0} max={100} value={percent} onChange={e => setPercent(e.target.value)} /></FormField>
        <FormField label="Süre (ay)"><input className="ui-input" type="number" min={1} max={36} value={months} onChange={e => setMonths(e.target.value)} /></FormField>
        <FormField label="Sebep (opsiyonel)"><input className="ui-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="Sadakat indirimi, kurumsal anlaşma..." /></FormField>
        <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', marginTop: 8 }}>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>{busy ? '...' : 'Uygula'}</Button>
        </div>
      </div>
    </Modal>
  );
}

function SendMessageModal({ company, onClose, onDone }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post('/vendor/companies/' + company.id + '/messages', { channel: 'email', subject, body });
      onDone();
    } catch (err) { alert(err.response?.data?.message || 'Hata'); setBusy(false); }
  };
  return (
    <Modal title="✉️ Mesaj Gönder" onClose={onClose}>
      <div style={{ padding: 18, display:'flex', flexDirection:'column', gap: 12 }}>
        <FormField label="Konu"><input className="ui-input" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Örn: Yeni özelliklerimiz" /></FormField>
        <FormField label="İçerik" required>
          <textarea className="ui-input" value={body} onChange={e => setBody(e.target.value)} rows={8}
                    placeholder="HTML desteklidir. Örn: <p>Sayın {{firma_adi}},</p>" />
        </FormField>
        <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', marginTop: 8 }}>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button variant="primary" onClick={submit} disabled={!body.trim() || busy}>{busy ? '...' : '🚀 Gönder'}</Button>
        </div>
      </div>
    </Modal>
  );
}
